import { create } from 'zustand';
import { Task, getTasks, markSeen as apiMarkSeen, markAllSeen as apiMarkAllSeen, dismissTask as apiDismissTask } from '../api/tasks';

const POLL_FAST = 5_000;   // 5 сек — есть pending/running задачи
const POLL_SLOW = 60_000;  // 60 сек — все задачи завершены

interface TaskState {
  tasks: Task[];
  unreadCount: number;
  loading: boolean;
  _pollTimer: ReturnType<typeof setTimeout> | null;
  _dropdownOpen: boolean;
  /** Типы задач, для которых НЕ открывать popup (страница сама обрабатывает) */
  _suppressedTypes: Set<string>;

  startPolling: () => void;
  stopPolling: () => void;
  pollTasks: () => Promise<void>;
  markSeen: (id: number) => Promise<void>;
  markAllSeen: () => Promise<void>;
  dismissTask: (id: number) => Promise<void>;
  setDropdownOpen: (open: boolean) => void;
  /** Подавить auto-open popup для задач данного типа */
  suppressType: (type: string) => void;
  /** Снять подавление */
  unsuppressType: (type: string) => void;
}

const useTaskStore = create<TaskState>((set, get) => ({
  tasks: [],
  unreadCount: 0,
  loading: false,
  _pollTimer: null,
  _dropdownOpen: false,
  _suppressedTypes: new Set(),

  startPolling: () => {
    if (get()._pollTimer) return; // уже запущен

    // Первый poll сразу
    get().pollTasks();

    const scheduleNext = () => {
      const { tasks } = get();
      const hasActive = tasks.some(t => t.status === 'pending' || t.status === 'running');
      const delay = hasActive ? POLL_FAST : POLL_SLOW;

      const timer = setTimeout(async () => {
        await get().pollTasks();
        // Планируем следующий только если polling не был остановлен
        if (get()._pollTimer) {
          scheduleNext();
        }
      }, delay);
      set({ _pollTimer: timer });
    };

    scheduleNext();
  },

  stopPolling: () => {
    const timer = get()._pollTimer;
    if (timer) {
      clearTimeout(timer);
      set({ _pollTimer: null });
    }
  },

  pollTasks: async () => {
    try {
      const newTasks = await getTasks();
      const prevTasks = get().tasks;

      // Проверяем: появились новые завершённые задачи?
      // 1) переход pending/running → done/error
      // 2) новая задача (не было в prevTasks), уже done/error и непрочитанная
      // Игнорируем подавленные типы (страница сама обрабатывает результат)
      const suppressedTypes = get()._suppressedTypes;
      const justFinished = prevTasks.length > 0 && newTasks.some(nt => {
        if (suppressedTypes.has(nt.type)) return false;
        const prev = prevTasks.find(pt => pt.id === nt.id);
        if (prev) {
          return (prev.status === 'running' || prev.status === 'pending')
            && (nt.status === 'done' || nt.status === 'error');
        }
        // Новая задача, которой раньше не было — уже терминальная и непрочитанная
        return (nt.status === 'done' || nt.status === 'error') && !nt.seen_at;
      });

      const unreadCount = newTasks.filter(t => t.seen_at === null).length;

      set({ tasks: newTasks, unreadCount });

      if (justFinished) {
        set({ _dropdownOpen: true });
      }
    } catch {
      // Молча игнорируем ошибки poll (сеть, 401 и т.д.)
    }
  },

  markSeen: async (id: number) => {
    // Оптимистичное обновление
    const prev = get().tasks;
    const now = new Date().toISOString();
    const updated = prev.map(t =>
      t.id === id && !t.seen_at ? { ...t, seen_at: now } : t
    );
    set({
      tasks: updated,
      unreadCount: updated.filter(t => t.seen_at === null).length,
    });

    try {
      await apiMarkSeen(id);
    } catch {
      // Откат
      set({
        tasks: prev,
        unreadCount: prev.filter(t => t.seen_at === null).length,
      });
    }
  },

  markAllSeen: async () => {
    const prev = get().tasks;
    const now = new Date().toISOString();
    // Важные задачи (с action_label) не помечаем — их нужно обработать вручную
    const updated = prev.map(t =>
      t.seen_at === null && !(t.status === 'done' && t.result_json?.action_label)
        ? { ...t, seen_at: now } : t
    );
    set({ tasks: updated, unreadCount: updated.filter(t => t.seen_at === null).length });

    try {
      await apiMarkAllSeen();
      // Рефетч чтобы poll не вернул старые данные
      const fresh = await getTasks();
      set({
        tasks: fresh,
        unreadCount: fresh.filter(t => t.seen_at === null).length,
      });
    } catch {
      set({
        tasks: prev,
        unreadCount: prev.filter(t => t.seen_at === null).length,
      });
    }
  },

  dismissTask: async (id: number) => {
    const prev = get().tasks;
    const filtered = prev.filter(t => t.id !== id);
    set({
      tasks: filtered,
      unreadCount: filtered.filter(t => t.seen_at === null).length,
    });

    try {
      await apiDismissTask(id);
    } catch {
      set({
        tasks: prev,
        unreadCount: prev.filter(t => t.seen_at === null).length,
      });
    }
  },

  setDropdownOpen: (open: boolean) => {
    set({ _dropdownOpen: open });
  },

  suppressType: (type: string) => {
    const next = new Set(get()._suppressedTypes);
    next.add(type);
    set({ _suppressedTypes: next });
  },

  unsuppressType: (type: string) => {
    const next = new Set(get()._suppressedTypes);
    next.delete(type);
    set({ _suppressedTypes: next });
  },
}));

export default useTaskStore;
